import { Badge, Button, Dropdown, Popover, Tooltip } from "antd";
import type { MenuProps } from "antd/lib";
import clsx from "clsx";
import type { CSSProperties } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import SVG_ICON from "@/assets/icons/svg";
import { AssistantPanel } from "@/components/AssistantPanel";
import AccessControl from "@/config/permission/AccessControl";
import { PERMISSION } from "@/config/permission/catalogue.v2";
import { useAuth } from "@/contexts/AuthContext";
import { ConsultationTabKey } from "@/pages/Consultation/constants";
import { ERoutesPathname } from "@/routers/config";
import { useUserStore } from "@/stores";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { getRoleColorClass } from "@/utils/roleColors";
import NotificationsPopoverContent from "./notifications/NotificationsPopoverContent";
import type { AppNotification } from "./notifications/types";
import { useRadNotifications } from "./notifications/useRadNotifications";

// custom style, set padding of li tag to 0
const dropdownStyles = {
  menuItem: {
    padding: 0,
  } as CSSProperties,
};

// create custom style class name
const dropdownMenuClass = `zero-padding-dropdown`;

// add global style
const globalStyle = `
  .${dropdownMenuClass} .ant-dropdown-menu-item {
    padding: 0 !important;
  }
`;

interface HeaderActionsProps {
  onAddPatient: () => void;
}

export default function HeaderActions({ onAddPatient }: HeaderActionsProps) {
  const { isAuthenticated, saveRedirectPath, signOutRedirect } = useAuth();
  const navigate = useNavigate();
  const { profile } = useUserStore();
  const { role } = useAdditionalInfo();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead } = useRadNotifications();

  const handleAuthAction = () => {
    if (isAuthenticated) {
      // Logout from the identity provider instead of immediately starting a new login.
      saveRedirectPath();
      signOutRedirect();
    } else {
      saveRedirectPath();
      navigate(ERoutesPathname.login);
    }
  };

  // Get user role from additional info store
  const userRole = role?.label || "No Role";

  // Get dynamic color class based on role
  const roleColorClass = getRoleColorClass(userRole);

  const items: MenuProps["items"] = [
    {
      key: "0",
      label: (
        <div className={clsx("py-[6px] px-[15px] text-center font-medium", roleColorClass)}>
          {userRole}
        </div>
      ),
      disabled: true,
    },
    {
      key: "1",
      label: <div className="py-[6px] px-[15px] text-center">{profile?.fullName}</div>,
      disabled: true,
    },
    {
      key: "2",
      label: (
        <Button className="w-full text-left justify-center" onClick={handleAuthAction} type="text">
          {isAuthenticated ? "Sign Out" : "Sign In"}
        </Button>
      ),
      style: dropdownStyles.menuItem,
    },
  ];

  const handleNotificationClick = (notification: AppNotification) => {
    markAsRead(notification.eventId);
    setNotificationsOpen(false);
    if (!notification.patientId) return;
    navigate(`/patient/consultation/${notification.patientId}`, {
      state: {
        openTabKey: ConsultationTabKey.Investigation,
        visitId: notification.visitId,
        accessionNumber: notification.accessionNumber,
        openEditOrder: true,
        navKey: Date.now(),
      },
    });
  };

  const handleViewAll = () => {
    setNotificationsOpen(false);
    navigate(ERoutesPathname.investigations);
  };

  return (
    <>
      <style>{globalStyle}</style>
      <div className="flex items-center gap-6">
        <AssistantPanel />

        <AccessControl
          do={PERMISSION.patient.CREATE}
          not={
            <Tooltip title="You don't have permission to add new patient">
              <button type="button" className="bg-transparent border-0 p-0 cursor-pointer" disabled>
                <img src={SVG_ICON.PlusBlackIcon} alt="plus" />
              </button>
            </Tooltip>
          }
        >
          <button
            type="button"
            onClick={onAddPatient}
            className="bg-transparent border-0 p-0 cursor-pointer"
          >
            <img src={SVG_ICON.PlusBlackIcon} alt="plus" />
          </button>
        </AccessControl>

        <Popover
          open={notificationsOpen}
          onOpenChange={setNotificationsOpen}
          trigger="click"
          placement="bottomRight"
          arrow={false}
          overlayInnerStyle={{ padding: 0 }}
          content={
            <NotificationsPopoverContent
              notifications={notifications}
              onViewAll={handleViewAll}
              onNotificationClick={handleNotificationClick}
            />
          }
        >
          <button
            type="button"
            className="relative bg-transparent border-0 p-0 cursor-pointer leading-none"
            aria-label="Notifications"
          >
            <Badge count={unreadCount} size="small" overflowCount={99} offset={[-2, 2]}>
              <img src={SVG_ICON.BellSimpleIcon} alt="bell" className="block" />
            </Badge>
          </button>
        </Popover>

        <Dropdown
          menu={{
            items,
            className: dropdownMenuClass,
          }}
          placement="topRight"
        >
          <img src={SVG_ICON.UserIcon} alt="user" className="cursor-pointer" />
        </Dropdown>
      </div>
    </>
  );
}
